/* eslint-disable no-param-reassign */
import * as utils from '../../../../util';
import type { Data } from '../../specProcessor';
import type { StandardChartLayerSpec } from '../../../../core/layer';
import type { Spec as DynamicRankingTwoWayHorizontalBarLayerSpec } from '../../../../layer/preset/bar/dynamicRankingTwoWayHorizontalBar';
import {
  BaseSpec,
  ILayerTypeSpec,
  createDataTypeValidator,
  createEncodingValidator,
  createLayerProcessor
} from '../baseProcessor';

export type Spec = BaseSpec;

// 扩展ILayerTypeSpec以包含__isEncodingValid属性
interface DynamicRankingTwoWayHorizontalBarLayerTypeWithFields extends ILayerTypeSpec {
  __isEncodingValid?: boolean;
}

type DynamicRankingTwoWayHorizontalBarLayerType = StandardChartLayerSpec &
  DynamicRankingTwoWayHorizontalBarLayerSpec &
  DynamicRankingTwoWayHorizontalBarLayerTypeWithFields;

// 创建动态排名双向条形图数据类型验证器
const validateDynamicRankingTwoWayHorizontalBarDataTypes = createDataTypeValidator(
  'dynamicRankingTwoWayHorizontalBar',
  [
    { field: 'x', type: 'number' },
    { field: 'x2', type: 'number' },
    { field: 'y', type: 'string' },
    { field: 'group', type: 'date' }
  ]
);

// 创建动态排名双向条形图编码验证器
const validateDynamicRankingTwoWayHorizontalBarEncoding = createEncodingValidator([
  { field: 'x' },
  { field: 'x2' },
  { field: 'y' },
  { field: 'group' }
]);

// 额外的检查函数 - 验证数据量是否满足要求
const additionalDynamicRankingTwoWayHorizontalBarChecks = (
  layer: DynamicRankingTwoWayHorizontalBarLayerType,
  data: Data
) => {
  const { dataIndex = 0, encoding } = layer;
  const values = data[dataIndex]?.values || [];

  if (values.length <= 1) {
    utils.logger.warn(
      'warning',
      `The data amount of the "${layer.type}" layer must be greater than 1`
    );
    layer.__isEncodingValid = false;
  }

  // 检查左侧X轴数据量
  const xValues = new Set(values.map(item => item[encoding.x]));
  if (xValues.size <= 1) {
    utils.logger.warn(
      'warning',
      `The amount of "${encoding.x}" values must be greater than 1 for "${layer.type}" layer`
    );
    layer.__isEncodingValid = false;
  }

  // 检查右侧X2轴数据量
  const x2Values = new Set(values.map(item => item[encoding.x2]));
  if (x2Values.size <= 1) {
    utils.logger.warn(
      'warning',
      `The amount of "${encoding.x2}" values must be greater than 1 for "${layer.type}" layer`
    );
    layer.__isEncodingValid = false;
  }

  // 检查Y轴数据量
  const yValues = new Set(values.map(item => item[encoding.y]));
  if (yValues.size <= 1) {
    utils.logger.warn(
      'warning',
      `The amount of "${encoding.y}" values must be greater than 1 for "${layer.type}" layer`
    );
    layer.__isEncodingValid = false;
  }

  // 检查Group数据量
  const groupValues = new Set(values.map(item => item[encoding.group]));
  if (groupValues.size <= 1) {
    utils.logger.warn(
      'warning',
      `The amount of "${encoding.group}" values must be greater than 1 for "${layer.type}" layer`
    );
    layer.__isEncodingValid = false;
  }
};

// 创建并导出动态排名双向条形图处理器
export const dynamicRankingTwoWayHorizontalBar =
  createLayerProcessor<DynamicRankingTwoWayHorizontalBarLayerType>(
    'dynamicRankingTwoWayHorizontalBar',
    validateDynamicRankingTwoWayHorizontalBarEncoding,
    validateDynamicRankingTwoWayHorizontalBarDataTypes,
    additionalDynamicRankingTwoWayHorizontalBarChecks
  );
